/**
 * payrollImport.js — lista e pagave e një periudhe, e sjellë nga Excel.
 *
 * ── Pse ekziston ─────────────────────────────────────────────────────────────
 * Pagat e një muaji hyjnë rresht-për-rresht te ekrani i listës. Për dhjetë
 * punëtorë kjo shkon; për njëqind — jo. Dhe shumica e firmave e kanë tashmë
 * shumën e dakorduar diku në një fletë Excel, shpesh të negociuar NETO, sepse
 * ajo është shifra për të cilën flitet me punëtorin.
 *
 * ── Të dy drejtimet, në të njëjtin skedar ────────────────────────────────────
 * Kolona `Baza` e vendos rresht-për-rresht se çfarë kuptimi ka `Shuma`:
 *
 *   BRUTO → Shuma është paga bazë; neto llogaritet prej saj (netFromGross).
 *   NETO  → Shuma është ajo që merr punëtori NË DORË, pas çdo ndalese të
 *           shënuar në atë rresht; paga bazë zgjidhet me kërkim binar
 *           (grossFromNet) derisa neto të bjerë në qindarkë.
 *
 * Të dyja kalojnë nga `utils/salaryCalc`, pra nga E NJËJTA formulë që përdor
 * ekrani. Një kalkulator i dytë këtu do të thoshte se importi dhe ekrani do të
 * jepnin dy paga të ndryshme për të njëjtin rresht ditën që ndryshon politika
 * fiskale — dhe askush nuk do ta dinte cila është e vërteta.
 *
 * ── Skedari ──────────────────────────────────────────────────────────────────
 * Periudha udhëton BRENDA skedarit (qelizat `Muaji` / `Viti`), jo vetëm te
 * emri i tij: një shabllon i marsit i ngarkuar te prilli duhet të kapet, jo të
 * shkruajë pagat e gabuara në heshtje. Parsuesi i lexon kolonat nga etiketat e
 * kokës e jo nga pozicionet — kolona e shtuar me dorë nuk e prish importin.
 */
import * as XLSX from 'xlsx-js-style';
import { netFromGross, grossFromNet, paramsFromRow } from './salaryCalc';
import { MONTHS_SQ } from './period';

/* ─── Paleta — e njëjta si eksportet e tjera të pagave ─────────────────────── */
const BRAND      = '3E8FA3';
const BRAND_DARK = '2C6E7E';
const INK_STRONG = '0F172A';
const MUTED      = '64748B';
const GRID       = 'E2E8F0';
const INPUT_BG   = 'FFFDF5';   // qelizat që pret të plotësohen
const REF_BG     = 'F1F5F9';   // qelizat vetëm për orientim

const FILL   = (rgb) => ({ patternType: 'solid', fgColor: { rgb } });
const BORDER = (rgb = GRID) => ({
  top:    { style: 'thin', color: { rgb } },
  bottom: { style: 'thin', color: { rgb } },
  left:   { style: 'thin', color: { rgb } },
  right:  { style: 'thin', color: { rgb } },
});

const S = {
  company:  { font: { bold: true, sz: 11, color: { rgb: MUTED } } },
  title:    { font: { bold: true, sz: 15, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND),
              alignment: { horizontal: 'left', vertical: 'center', indent: 1 } },
  metaHead: { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND_DARK),
              alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER('FFFFFF') },
  metaVal:  { font: { bold: true, sz: 11, color: { rgb: INK_STRONG } }, fill: FILL(INPUT_BG),
              alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER('D9B382') },
  note:     { font: { italic: true, sz: 9, color: { rgb: MUTED } } },
  band:     { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND_DARK),
              alignment: { horizontal: 'center', vertical: 'center' }, border: BORDER('FFFFFF') },
  head:     { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND),
              alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: BORDER('9FC4CE') },
  total:    { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: FILL(BRAND_DARK),
              alignment: { vertical: 'center' }, border: BORDER(BRAND_DARK) },
  glossH:   { font: { bold: true, sz: 10, color: { rgb: INK_STRONG } }, fill: FILL('EEF2F6'),
              alignment: { vertical: 'center', indent: 1 } },
  glossK:   { font: { bold: true, sz: 9, color: { rgb: INK_STRONG } }, alignment: { vertical: 'top' } },
  glossV:   { font: { sz: 9, color: { rgb: '334155' } }, alignment: { vertical: 'top', wrapText: true } },
};

const MONEY_FMT = '#,##0.00';

/**
 * Modeli i kolonave — një burim i vetëm për shabllonin DHE për parsuesin.
 *
 * `ref: true` do të thotë "vetëm për orientim": mbushet nga sistemi që rreshti
 * të njihet, dhe nuk pritet ta prekë kush. Ngjyra e sfondit e thotë të njëjtën
 * gjë në skedar, që rregulli të mos jetojë vetëm në këtë dokumentacion.
 */
export const IMPORT_COLUMNS = [
  { key: 'no',             label: 'Nr.',              w: 5,  group: '',                   ref: true },
  { key: 'nid',            label: 'Numri personal',   w: 16, group: 'PUNONJËSI',          ref: true },
  { key: 'firstName',      label: 'Emri',             w: 15, group: 'PUNONJËSI',          ref: true },
  { key: 'lastName',       label: 'Mbiemri',          w: 15, group: 'PUNONJËSI',          ref: true },
  { key: 'basis',          label: 'Baza',             w: 10, group: 'PAGA' },
  { key: 'amount',         label: 'Shuma (€)',        w: 13, group: 'PAGA',               money: true, total: true },
  { key: 'overtime',       label: 'Orë shtesë (€)',   w: 13, group: 'SHTESAT',            money: true, total: true },
  { key: 'bonus',          label: 'Bonus (€)',        w: 12, group: 'SHTESAT',            money: true, total: true },
  { key: 'advances',       label: 'Avanci (€)',       w: 12, group: 'NDALESAT',           money: true, total: true },
  { key: 'penalties',      label: 'Ndalesa (€)',      w: 12, group: 'NDALESAT',           money: true, total: true },
  // Sigurimi vullnetar ruhet te KARTELA e punëtorit (`workers.health_ins_*`), jo
  // te rreshti i pagës — `salaryToDb` s'ka kolonë ku ta shkruajë. Prandaj del si
  // kolonë orientimi: hyn te llogaritja (te rreshtat NETO ngre pagën bruto aq sa
  // duhet), por vlera merret nga kartela. Një kuti ku shkruan një shifër që nuk
  // ruhet dot do të tregonte një neto që paga s'do ta jepte kurrë.
  { key: 'healthPersonal', label: 'Sig. personal (€)',w: 14, group: 'SIGURIMI VULLNETAR (nga kartela)', money: true, total: true, ref: true },
  { key: 'healthFamily',   label: 'Sig. familjar (€)',w: 14, group: 'SIGURIMI VULLNETAR (nga kartela)', money: true, total: true, ref: true },
];

const NC = IMPORT_COLUMNS.length;
export const SHEET_NAME = 'Pagat';

/* ─── Normalizimi i teksteve ───────────────────────────────────────────────── */

/** Pa diakritikë, pa shenja, me shkronja të vogla — që 'Nëntor' të gjejë 'nentor'. */
export const slug = (v) =>
  String(v ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

/** Çelës emri i pandjeshëm ndaj rendit: 'Sejdiu Fisnik' == 'Fisnik Sejdiu'. */
export const nameKey = (v) =>
  String(v ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .split(/\s+/).filter(Boolean).sort().join(' ');

/** Vetëm shifrat — numrat personalë vijnë me hapësira, viza ose si numër Excel. */
const nidKey = (v) => String(v ?? '').replace(/\D+/g, '');

/**
 * Numër nga një qelizë Excel-i ose nga tekst i shkruar me dorë.
 *
 * Pranon të dy konventat dhjetore: '1.234,56' (evropiane) dhe '1,234.56'
 * (angleze). Dallimi bëhet nga shenja e FUNDIT — ajo është presja dhjetore,
 * çfarëdo qoftë. Pa këtë, '1.234,56' do të lexohej 1.23 dhe një pagë prej
 * 1234 € do të hynte si 1 €, pa asnjë gabim në ekran.
 */
export function toAmount(v) {
  if (v === null || v === undefined || v === '') return 0;
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  let s = String(v).trim().replace(/[\u20AC\s\u00A0\u202F]/g, '');
  if (!s) return 0;
  const lastComma = s.lastIndexOf(',');
  const lastDot   = s.lastIndexOf('.');
  if (lastComma > -1 && lastDot > -1) {
    s = lastComma > lastDot
      ? s.replace(/\./g, '').replace(',', '.')     // 1.234,56
      : s.replace(/,/g, '');                        // 1,234.56
  } else if (lastComma > -1) {
    // Një presje e vetme: dhjetore kur ndan 1–2 shifra, ndryshe ndarëse mijësh.
    s = /,\d{1,2}$/.test(s) ? s.replace(',', '.') : s.replace(/,/g, '');
  }
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

/** 'NETO' | 'neto' | 'N' | 'net' → 'net'; gjithçka tjetër → 'gross'. */
export function toBasis(v, fallback = 'gross') {
  const s = slug(v);
  if (!s) return fallback;
  if (s === 'n' || s.startsWith('net')) return 'net';
  if (s === 'b' || s === 'g' || s.startsWith('brut') || s.startsWith('gros')) return 'gross';
  return fallback;
}

/** Emër ose numër muaji → 1..12, ose 0 kur nuk njihet. */
export function toMonth(v) {
  const n = Number(v);
  if (Number.isInteger(n) && n >= 1 && n <= 12) return n;
  const s = slug(v);
  const i = MONTHS_SQ.findIndex((m) => slug(m) === s);
  return i > -1 ? i + 1 : 0;
}

/* ═══ Shablloni ════════════════════════════════════════════════════════════ */

const pad = (row) => { const out = row.slice(); while (out.length < NC) out.push(''); return out; };

/**
 * Libri i shabllonit për një periudhë.
 *
 * @param {object}   o
 * @param {number}   o.year    viti i listës
 * @param {number}   o.month   muaji 1..12
 * @param {string}   o.basis   'gross' | 'net' — parazgjedhja e kolonës `Baza`
 * @param {Array}    o.workers punëtorët që hyjnë të parambushur
 * @param {string}   o.company emri i firmës për kokën
 * @param {object}   o.rates   normat aktive, vetëm për fletën e udhëzimeve
 */
export function buildImportWorkbook({ year, month, basis = 'gross', workers = [], company = '', rates = {} } = {}) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheetData({ year, month, basis, workers, company }), SHEET_NAME);
  XLSX.utils.book_append_sheet(wb, sheetGuide({ basis, rates }), 'Udhëzime');
  return wb;
}

function sheetData({ year, month, basis, workers, company }) {
  const aoa = [];
  const merges = [];
  const ops = [];      // { r, c, s, z }
  const heights = [];
  const last = NC - 1;
  const push = (row) => (aoa.push(row), aoa.length - 1);
  const style = (r, c, s, z) => ops.push({ r, c, s, z });

  const companyR = push([company || 'Nexuma']);
  merges.push({ s: { r: companyR, c: 0 }, e: { r: companyR, c: last } });
  style(companyR, 0, S.company);

  const titleR = push(['LISTA E PAGAVE — SHABLLON IMPORTI']);
  merges.push({ s: { r: titleR, c: 0 }, e: { r: titleR, c: last } });
  style(titleR, 0, S.title);
  heights[titleR] = { hpt: 26 };

  // Periudha rri në qeliza të veta, jo vetëm te titulli: importi e lexon prej
  // andej dhe e krahason me periudhën e zgjedhur në ekran.
  const metaHeadR = push(pad(['', 'Muaji', 'Viti', 'Baza e paracaktuar']));
  [1, 2, 3].forEach((c) => style(metaHeadR, c, S.metaHead));
  const metaValR = push(pad(['', MONTHS_SQ[(Number(month) || 1) - 1] || '', Number(year) || '', basis === 'net' ? 'NETO' : 'BRUTO']));
  [1, 2, 3].forEach((c) => style(metaValR, c, S.metaVal));
  heights[metaValR] = { hpt: 20 };

  const noteR = push(['Plotëso kolonat me sfond të verdhë. Kolona "Baza" pranon BRUTO ose NETO për çdo rresht — bosh do të thotë parazgjedhja lart. Kolonat gri identifikojnë punëtorin; mos i ndrysho.']);
  merges.push({ s: { r: noteR, c: 0 }, e: { r: noteR, c: last } });
  style(noteR, 0, S.note);

  push([]);

  const bandR = push(pad(IMPORT_COLUMNS.map((c, i) => (i === 0 || IMPORT_COLUMNS[i - 1].group !== c.group ? c.group : ''))));
  let from = 0;
  IMPORT_COLUMNS.forEach((c, i) => {
    style(bandR, i, S.band);
    const isLast = i === last || IMPORT_COLUMNS[i + 1].group !== c.group;
    if (isLast) { if (i > from) merges.push({ s: { r: bandR, c: from }, e: { r: bandR, c: i } }); from = i + 1; }
  });

  const headR = push(IMPORT_COLUMNS.map((c) => c.label));
  IMPORT_COLUMNS.forEach((_, c) => style(headR, c, S.head));
  heights[headR] = { hpt: 28 };

  const body = (col, zebra) => ({
    font: { sz: 10, color: { rgb: INK_STRONG } },
    fill: FILL(col.ref ? REF_BG : INPUT_BG),
    alignment: { horizontal: col.money ? 'right' : (col.key === 'no' || col.key === 'basis' ? 'center' : 'left'),
                 vertical: 'center', indent: col.money || col.key === 'no' || col.key === 'basis' ? 0 : 1 },
    border: BORDER(zebra ? 'DDE5EC' : GRID),
  });

  const list = workers || [];
  list.forEach((w, i) => {
    const r = push(IMPORT_COLUMNS.map((c) => {
      if (c.key === 'no')        return i + 1;
      if (c.key === 'nid')       return w.nid || '';
      if (c.key === 'firstName') return w.firstName || '';
      if (c.key === 'lastName')  return w.lastName || '';
      if (c.key === 'basis')     return basis === 'net' ? 'NETO' : 'BRUTO';
      if (c.key === 'amount')         return round2(w.amount || 0);
      if (c.key === 'healthPersonal') return round2(w.healthPersonal || 0);
      if (c.key === 'healthFamily')   return round2(w.healthFamily || 0);
      return 0;
    }));
    IMPORT_COLUMNS.forEach((c, ci) => style(r, ci, body(c, i % 2 === 1), c.money ? MONEY_FMT : undefined));
  });

  // Totali me formula: shabllon i mbushur me dorë duhet të mblidhet vetë, që
  // ai që e plotëson ta krahasojë me shifrën e vet para se ta ngarkojë.
  const firstDataR = headR + 1;
  const lastDataR  = firstDataR + Math.max(list.length, 1) - 1;
  const totalR = push(IMPORT_COLUMNS.map((c, i) => {
    if (i === 0) return 'TOTALI';
    if (!c.total || !list.length) return '';
    const col = XLSX.utils.encode_col(i);
    return { t: 'n', f: `SUM(${col}${firstDataR + 1}:${col}${lastDataR + 1})` };
  }));
  IMPORT_COLUMNS.forEach((_, c) => style(totalR, c, S.total, IMPORT_COLUMNS[c].total ? MONEY_FMT : undefined));
  heights[totalR] = { hpt: 20 };

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols']   = IMPORT_COLUMNS.map((c) => ({ wch: c.w }));
  ws['!merges'] = merges;
  ws['!rows']   = heights;
  if (list.length) {
    ws['!autofilter'] = {
      ref: `${XLSX.utils.encode_cell({ r: headR, c: 0 })}:${XLSX.utils.encode_cell({ r: lastDataR, c: last })}`,
    };
  }
  ops.forEach(({ r, c, s, z }) => {
    const addr = XLSX.utils.encode_cell({ r, c });
    if (!ws[addr]) return;
    if (s) ws[addr].s = s;
    if (z) ws[addr].z = z;
  });
  return ws;
}

function sheetGuide({ basis, rates }) {
  const pct = (v, d) => `${Number.isFinite(Number(v)) && Number(v) ? Number(v) : d}%`;
  const rows = [
    ['SI PLOTËSOHET', ''],
    ['Baza', 'BRUTO ose NETO, për çdo rresht veç. Bosh → merret parazgjedhja te fleta "Pagat".'],
    ['BRUTO', 'Shuma është paga bazë e kontratës. Kontributet, tatimi dhe sigurimi zbriten prej saj.'],
    ['NETO', 'Shuma është ajo që merr punëtori NË DORË — pas tatimit, kontributit, sigurimit, avancit dhe ndalesave të këtij rreshti. Sistemi gjen vetë pagën bruto që e jep atë neto.'],
    ['Shuma', 'Vetëm numri, pa "€". Presja ose pika dhjetore pranohen të dyja.'],
    ['Orë shtesë (€)', 'Vlera në euro e orëve shtesë, jo numri i orëve. Lëre 0 nëse i mbush nga orari pas importit.'],
    ['Avanci / Ndalesa', 'Zbriten nga neto. Te rreshtat NETO ato hyjnë në llogaritje — paga bruto ngrihet aq sa duhet që shuma në dorë të mbetet ajo që shkrove.'],
    ['Sig. personal / familjar', 'Sigurimi shëndetësor vullnetar. Vjen nga kartela e punëtorit dhe nuk ndryshohet këtu — shfaqet sepse hyn te llogaritja: te rreshtat NETO, paga bruto ngrihet aq sa duhet për ta mbuluar.'],
    ['', ''],
    ['SI NJIHET PUNËTORI', ''],
    ['Numri personal', 'Përputhja kryesore. Mos e fshi — pa të rreshti kërkon emrin.'],
    ['Emri / Mbiemri', 'Përputhja rezervë kur numri personal mungon. Rendi nuk ka rëndësi.'],
    ['Rreshta të rinj', 'Mund të shtosh rreshta për punëtorë që s\'janë në shabllon — mjafton numri personal ose emri i plotë.'],
    ['', ''],
    ['ÇFARË NDODH ME IMPORTIN', ''],
    ['Periudha', 'Merret nga qelizat "Muaji" dhe "Viti". Nëse s\'përputhet me periudhën e zgjedhur, importi ndalon dhe e thotë.'],
    ['Lista', 'Nëse lista e periudhës nuk ekziston, krijohet. Nëse ekziston, rreshtat e punëtorëve që ndodhen në skedar përditësohen; të tjerët mbeten siç ishin.'],
    ['Llogaritja', 'E njëjta formulë si ekrani i pagave — importi nuk ka aritmetikë të vetën.'],
    ['', ''],
    ['NORMAT AKTIVE', ''],
    ['Pensioni (punëtori)', pct(rates.pensionEmployee, 5)],
    ['Pensioni (punëdhënësi)', pct(rates.pensionEmployer, 5)],
    ['Sig. shëndetësor (punëtori)', pct(rates.healthInsEmployee, 3.5)],
    ['Brezat tatimorë', (rates.ranges || []).length
      ? (rates.ranges || []).map((r) => {
          const to = r.To ?? r.to ?? r.MaxAmount;
          const hi = (to === null || to === undefined || to === '' || Number(to) <= 0) ? '∞' : Number(to);
          return `${Number(r.From ?? r.from ?? r.MinAmount ?? 0)}–${hi}: ${Number(r.Tax ?? r.tax ?? r.percent ?? 0)}%`;
        }).join('  ·  ')
      : 'Nuk u lexuan brezat — kontrollo politikën fiskale aktive.'],
    ['Parazgjedhja e këtij shablloni', basis === 'net' ? 'NETO' : 'BRUTO'],
  ];
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = [{ wch: 30 }, { wch: 92 }];
  ws['!rows'] = rows.map((r) => (r[1] && r[1].length > 90 ? { hpt: 30 } : undefined));
  rows.forEach((r, i) => {
    const a = XLSX.utils.encode_cell({ r: i, c: 0 });
    const b = XLSX.utils.encode_cell({ r: i, c: 1 });
    const isSection = r[0] && !r[1];
    if (ws[a]) ws[a].s = isSection ? S.glossH : S.glossK;
    if (ws[b]) ws[b].s = S.glossV;
    if (isSection) (ws['!merges'] = ws['!merges'] || []).push({ s: { r: i, c: 0 }, e: { r: i, c: 1 } });
  });
  return ws;
}

export const importFileName = ({ year, month }) =>
  `lista-e-pagave-import-${year || ''}-${String(month || '').padStart(2, '0')}.xlsx`;

export function downloadImportTemplate(opts = {}) {
  XLSX.writeFile(buildImportWorkbook(opts), importFileName(opts));
}

/* ═══ Parsuesi ═════════════════════════════════════════════════════════════ */

/** Etiketat e pranuara për çdo kolonë — të shkruara si `slug`. */
const HEADER_ALIASES = {
  no:             ['nr', 'no', 'numri'],
  nid:            ['numripersonal', 'nid', 'personalnumber', 'numripersonal', 'idnumri'],
  firstName:      ['emri', 'firstname'],
  lastName:       ['mbiemri', 'lastname'],
  fullName:       ['emriimbiemri', 'emridhembiemri', 'punetori', 'punonjesi', 'fullname', 'emriembiemri'],
  basis:          ['baza', 'bazabrutoneto', 'basis', 'llojii', 'lloji'],
  amount:         ['shuma', 'shumae', 'paga', 'pagabaze', 'amount', 'vlera'],
  overtime:       ['oreshtese', 'oreshtesa', 'oreshtesee', 'overtime', 'shtesa'],
  bonus:          ['bonus', 'bonuse', 'bonusi'],
  advances:       ['avanci', 'avanc', 'avancet', 'advances', 'paradhenie'],
  penalties:      ['ndalesa', 'ndalesat', 'penalitete', 'penalties'],
  healthPersonal: ['sigpersonal', 'sigurimipersonal', 'sigurimishendetesorpersonal', 'healthpersonal'],
  healthFamily:   ['sigfamiljar', 'sigurimifamiljar', 'sigurimishendetesorfamiljar', 'healthfamily'],
};

/** 'Shuma (€)' → 'shuma'; heq njësinë që etiketa të përputhet me aliaset. */
const headerSlug = (v) => slug(String(v ?? '').replace(/\(.*?\)/g, ''));

const MAX_META_SCAN = 12;

/**
 * Lexon një libër Excel-i të ngarkuar.
 *
 * @param {ArrayBuffer|Uint8Array} data
 * @returns {{ ok: boolean, error?: string, year: number, month: number,
 *             basis: string, rows: Array, sheet: string }}
 */
export function parseImportWorkbook(data) {
  let wb;
  try {
    wb = XLSX.read(data, { type: 'array' });
  } catch {
    return { ok: false, error: 'Skedari nuk u lexua dot — a është vërtet .xlsx apo .xls?', rows: [] };
  }
  const name = wb.SheetNames.includes(SHEET_NAME) ? SHEET_NAME : wb.SheetNames[0];
  const ws = name && wb.Sheets[name];
  if (!ws) return { ok: false, error: 'Skedari nuk ka asnjë fletë.', rows: [] };

  const grid = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });

  // ── Periudha: etiketë e ndjekur nga vlera poshtë saj ose djathtas ─────────
  const findMeta = (aliases) => {
    for (let r = 0; r < Math.min(grid.length, MAX_META_SCAN); r++) {
      const row = grid[r] || [];
      for (let c = 0; c < row.length; c++) {
        if (!aliases.includes(headerSlug(row[c]))) continue;
        const below = (grid[r + 1] || [])[c];
        if (below !== '' && below !== undefined && below !== null) return below;
        for (let k = c + 1; k < row.length; k++) {
          if (row[k] !== '' && row[k] !== undefined && row[k] !== null) return row[k];
        }
      }
    }
    return '';
  };
  const month = toMonth(findMeta(['muaji', 'muajii', 'month', 'periudha']));
  const year  = Number(String(findMeta(['viti', 'vitii', 'year'])).replace(/\D+/g, '')) || 0;
  const basis = toBasis(findMeta(['bazaeparacaktuar', 'bazaparazgjedhur', 'baza', 'defaultbasis']), 'gross');

  // ── Koka: rreshti me më së shumti etiketa të njohura ──────────────────────
  let headerRow = -1, colMap = {}, bestHits = 0;
  for (let r = 0; r < Math.min(grid.length, 40); r++) {
    const row = grid[r] || [];
    const map = {};
    let hits = 0;
    row.forEach((cell, c) => {
      const s = headerSlug(cell);
      if (!s) return;
      for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
        if (aliases.includes(s) && map[key] === undefined) { map[key] = c; hits++; break; }
      }
    });
    // `amount` është kolona pa të cilën importi s'ka kuptim — pa të, rreshti
    // ndoshta është banda e grupeve, jo koka.
    if (hits > bestHits && map.amount !== undefined) { bestHits = hits; headerRow = r; colMap = map; }
  }
  if (headerRow < 0) {
    return { ok: false, rows: [], year, month, basis, sheet: name,
             error: 'Kolonat nuk u njohën. Përdor shabllonin — duhet të paktën "Shuma" dhe "Numri personal" ose "Emri".' };
  }
  if (colMap.nid === undefined && colMap.fullName === undefined &&
      (colMap.firstName === undefined && colMap.lastName === undefined)) {
    return { ok: false, rows: [], year, month, basis, sheet: name,
             error: 'Mungon kolona që e njeh punëtorin — "Numri personal" ose "Emri"/"Mbiemri".' };
  }

  const at = (row, key) => (colMap[key] === undefined ? '' : row[colMap[key]]);
  const rows = [];
  for (let r = headerRow + 1; r < grid.length; r++) {
    const row = grid[r] || [];
    const nid   = String(at(row, 'nid') ?? '').trim();
    const first = String(at(row, 'firstName') ?? '').trim();
    const lastN = String(at(row, 'lastName') ?? '').trim();
    const full  = String(at(row, 'fullName') ?? '').trim();
    const label = full || [first, lastN].filter(Boolean).join(' ').trim();
    // Rreshti i totalit dhe rreshtat bosh nuk kanë as numër personal as emër.
    if (!nidKey(nid) && !label) continue;
    if (/^total/i.test(label)) continue;
    rows.push({
      excelRow: r + 1,
      nid,
      firstName: first,
      lastName:  lastN,
      fullName:  label,
      basis:     toBasis(at(row, 'basis'), basis),
      basisGiven: !!slug(at(row, 'basis')),
      amount:         toAmount(at(row, 'amount')),
      overtime:       toAmount(at(row, 'overtime')),
      bonus:          toAmount(at(row, 'bonus')),
      advances:       toAmount(at(row, 'advances')),
      penalties:      toAmount(at(row, 'penalties')),
      healthPersonal: toAmount(at(row, 'healthPersonal')),
      healthFamily:   toAmount(at(row, 'healthFamily')),
    });
  }

  return { ok: true, year, month, basis, rows, sheet: name, columns: Object.keys(colMap) };
}

/* ═══ Përputhja me punëtorët ═══════════════════════════════════════════════ */

/** Indeks kërkimi mbi punëtorët e ngarkuar — numër personal dhe emër. */
export function buildWorkerIndex(workers = []) {
  const byNid = new Map();
  const byName = new Map();
  workers.forEach((w) => {
    const id = w?.Id ?? w?.id ?? w?.ID;
    if (!id) return;
    const nid = nidKey(w.PersonalNumber || w.personal_number || w.NID);
    if (nid && !byNid.has(nid)) byNid.set(nid, w);
    const nm = nameKey(w.FullName || `${w.FirstName || ''} ${w.LastName || ''}`);
    if (nm) {
      // Emri i dyfishtë nuk zgjidhet me hamendje: shënohet si i paqartë.
      if (byName.has(nm)) byName.set(nm, 'ambiguous');
      else byName.set(nm, w);
    }
  });
  return { byNid, byName };
}

/** @returns {{ worker: object|null, by: 'nid'|'name'|null, reason?: string }} */
export function matchWorker(row, index) {
  const nid = nidKey(row.nid);
  if (nid) {
    const hit = index.byNid.get(nid);
    if (hit) return { worker: hit, by: 'nid' };
  }
  const nm = nameKey(row.fullName || `${row.firstName || ''} ${row.lastName || ''}`);
  if (nm) {
    const hit = index.byName.get(nm);
    if (hit === 'ambiguous') return { worker: null, by: null, reason: 'Dy punëtorë me të njëjtin emër — shto numrin personal.' };
    if (hit) return { worker: hit, by: 'name' };
  }
  return { worker: null, by: null, reason: nid ? 'Numri personal nuk u gjet te punëtorët aktivë.' : 'Punëtori nuk u njoh.' };
}

/* ═══ Llogaritja ═══════════════════════════════════════════════════════════ */

/**
 * Një rresht i importit → paga bazë dhe zbërthimi i saj.
 *
 * Të dy drejtimet kalojnë nga `salaryCalc`; këtu vendoset vetëm cili nga të dy.
 * `paramsFromRow` pret emrat e fushave të rreshtit të pagës, prandaj rreshti i
 * importit përkthehet në atë formë para se të llogaritet.
 */
export function solveImportRow(row, { policy = {}, ranges = [], worker = null } = {}) {
  const salaryRow = {
    HourSalary:        row.overtime,
    Bonus:             row.bonus,
    Advances:          row.advances,
    Penalties:         row.penalties,
    // Nga kartela e punëtorit, jo nga skedari: aty i lexon edhe ekrani i pagave
    // (`getSalariesByListId` i sjell me bashkim), ndaj parashikimi këtu dhe
    // rreshti i ruajtur tregojnë të njëjtën neto.
    HealthInsPersonal: Number(worker?.HealthInsPersonal ?? row.healthPersonal) || 0,
    HealthInsFamily:   Number(worker?.HealthInsFamily   ?? row.healthFamily)   || 0,
    EmployeeTax: Number(worker?.EmployeeTax) || 0,
    EmployerTax: Number(worker?.EmployerTax) || 0,
  };
  const params = paramsFromRow(salaryRow, policy, ranges);
  const amount = Number(row.amount) || 0;
  if (amount <= 0) return { ok: false, error: 'Shuma mungon ose është 0.' };

  if (row.basis === 'net') {
    const solved = grossFromNet(amount, params);
    if (!solved) {
      return { ok: false, error: 'Kjo neto nuk arrihet me këto ndalesa — kontrollo avancin dhe ndalesat.' };
    }
    // Kërkimi binar mbyllet me tolerancë; ndryshimi mbi një cent do të thotë se
    // shifra e shkruar nuk u arrit dhe përdoruesi duhet ta dijë.
    const drift = round2(Math.abs(solved.net - amount));
    return { ok: true, base: solved.base, totals: solved, drift: drift > 0.01 ? drift : 0 };
  }
  const totals = netFromGross(amount, params);
  return { ok: true, base: round2(amount), totals, drift: 0 };
}

/**
 * Skedari i lexuar + punëtorët + politika → rreshtat e gatshëm për ekran.
 *
 * Çdo rresht del me `status`: 'ok' | 'error' | 'duplicate'. Asnjë nuk hidhet
 * pa u treguar — një rresht i humbur në heshtje është një pagë e pashkruar.
 */
export function prepareImportRows(parsed, { workers = [], policy = {}, ranges = [] } = {}) {
  const index = buildWorkerIndex(workers);
  const seen = new Map();
  return (parsed?.rows || []).map((row) => {
    const { worker, by, reason } = matchWorker(row, index);
    if (!worker) return { ...row, status: 'error', error: reason || 'Punëtori nuk u njoh.' };

    const workerId = worker.Id ?? worker.id ?? worker.ID;
    if (seen.has(workerId)) {
      return { ...row, worker, workerId, matchedBy: by, status: 'duplicate',
               error: `I njëjti punëtor edhe te rreshti ${seen.get(workerId)}.` };
    }
    seen.set(workerId, row.excelRow);

    const solved = solveImportRow(row, { policy, ranges, worker });
    if (!solved.ok) return { ...row, worker, workerId, matchedBy: by, status: 'error', error: solved.error };

    return {
      ...row, worker, workerId, matchedBy: by, status: 'ok',
      base: solved.base, totals: solved.totals, drift: solved.drift,
      workerName: worker.FullName || `${worker.FirstName || ''} ${worker.LastName || ''}`.trim(),
    };
  });
}

/** Përmbledhja që shfaqet mbi tabelën e kontrollit. */
export function summarize(rows = []) {
  const ok = rows.filter((r) => r.status === 'ok');
  return {
    total:     rows.length,
    ready:     ok.length,
    errors:    rows.filter((r) => r.status === 'error').length,
    duplicates:rows.filter((r) => r.status === 'duplicate').length,
    netRows:   ok.filter((r) => r.basis === 'net').length,
    grossRows: ok.filter((r) => r.basis === 'gross').length,
    base:      round2(ok.reduce((s, r) => s + (r.base || 0), 0)),
    gross:     round2(ok.reduce((s, r) => s + (r.totals?.gross || 0), 0)),
    net:       round2(ok.reduce((s, r) => s + (r.totals?.net || 0), 0)),
    employerCost: round2(ok.reduce((s, r) => s + (r.totals?.employerCost || 0), 0)),
  };
}
